<template>
  <div class="upload-wrapper" style="display: flex; flex-wrap: wrap; gap: 10px">
    <!-- 单图模式 -->
    <div v-if="previewUrl" class="upload-preview" :style="`width: ${width}px; height: ${height}px`">
      <img v-if="isImage(previewUrl)" :src="previewUrl" :alt="altText" @error="handleImageError" />
      <video v-else-if="isVideo(previewUrl)" :src="previewUrl" :alt="altText" :controls="false"></video>
      <div class="overlay" @click="removeImage">
        <i class="el-icon-delete" :title="$t('components.CustomUploadImg.837872-0')"></i>
      </div>
    </div>
    <!-- 多图模式 -->
    <template v-if="previewList.length">
      <div
        v-for="(item, index) in previewList"
        :key="index"
        class="upload-preview"
        :style="`width: ${width}px; height: ${height}px`"
      >
        <img v-if="isImage(item)" :src="item" :alt="altText" @error="handleImageError" />
        <video v-else-if="isVideo(item)" :src="item" :alt="altText" :controls="false"></video>
        <div class="overlay">
          <i @click="removeImage(index)" class="el-icon-delete" :title="$t('components.CustomUploadImg.837872-0')"></i>
        </div>
      </div>
    </template>
    <!-- 上传按钮 -->
    <div
      v-if="(limit === 1 && !previewUrl) || (limit > 1 && previewList.length < limit)"
      class="upload-button"
      :style="`width: ${width}px; height: ${height}px`"
      @click="triggerUpload"
    >
      <i v-if="!loading" class="el-icon-plus"></i>
      <div v-else class="upload-loading">
        <i class="el-icon-loading"></i>
      </div>
    </div>
    <!-- 使用 :key="inputKey" 动态控制文件输入的重新渲染 -->
    <input
      ref="fileInput"
      type="file"
      :key="inputKey"
      style="display: none"
      :multiple="limit > 1"
      :accept="accept"
      @change="uploadFile"
    />
  </div>
</template>

<script>
  import axios from "axios";

  export default {
    props: {
      action: { type: String, required: true },
      url: { type: String, default: "" },
      list: { type: Array, default: () => [] },
      headers: { type: Object, default: () => ({}) },
      limit: { type: Number, default: 1 },
      accept: { type: String, default: "" },
      isType: { type: Boolean, default: false }, // 新增 isType 属性
      width: { type: Number, default: 148 }, // 图片宽度
      height: { type: Number, default: 148 } // 图片高度
    },
    data() {
      return {
        loading: false,
        altText: "img",
        previewUrl: "", // 单图预览URL
        previewList: [], // 多图预览URL列表
        inputKey: Date.now() // 用于控制文件输入的重新渲染
      };
    },
    watch: {
      // 监听单图模式的url
      url(newVal) {
        if (this.limit === 1) {
          this.previewUrl = newVal;
        }
      },
      // 监听多图模式的list
      list(newVal) {
        if (this.limit > 1) {
          this.previewList = [...newVal];
        }
      }
    },
    mounted() {
      // 初始化回显数据
      if (this.limit === 1 && this.url) {
        this.previewUrl = this.url; // 单图回显
      } else if (this.limit > 1 && this.list.length) {
        this.previewList = [...this.list]; // 多图回显
      }
    },
    methods: {
      isImage(url) {
        return /\.(jpeg|jpg|gif|png|webp)$/i.test(url);
      },
      isVideo(url) {
        return /\.(mp4|webm|ogg)$/i.test(url);
      },
      triggerUpload() {
        this.$refs.fileInput.click();
      },
      async uploadFile(event) {
        const files = event.target.files;
        if (!files.length) return;
        this.loading = true;

        const uploadPromises = Array.from(files)
          .slice(0, this.limit - this.previewList.length)
          .map(file => {
            const formData = new FormData();
            formData.append("file", file);

            // 如果 isType 为 true，根据文件类型添加 type 参数
            if (this.isType) {
              const type = this.isImage(file.name) ? 0 : 1; // 0 图片，1 视频
              formData.append("type", type);
            }

            return axios
              .post(this.action, formData, {
                headers: {
                  "Content-Type": "multipart/form-data",
                  ...this.headers
                }
              })
              .then(response => {
                if (response.data.code === 0) {
                  const blobUrl = URL.createObjectURL(file);
                  if (this.limit === 1) {
                    this.previewUrl = blobUrl;
                    this.$emit("update:url", response.data.data); // 更新单图URL
                  } else {
                    this.previewList.push(blobUrl);
                    this.list.push(response.data.data); // 更新多图的URL
                    this.$emit("update:list", this.list); // 更新list
                  }
                } else {
                  this.$notify.error({
                    title: this.$t('components.CustomUploadImg.837872-1'),
                    message: response.data.msg
                  });
                }
              })
              .catch(error => {
                this.$notify.error({
                  title: this.$t('components.CustomUploadImg.837872-1'),
                  message: error.message
                });
              });
          });

        await Promise.all(uploadPromises);
        this.loading = false;

        // 重新生成文件输入的 key，强制重新渲染 input 元素
        this.inputKey = Date.now();
      },
      handleImageError(event) {
        event.target.src = require("@/assets/img/imgerror.png"); // 替换为占位图路径
      },
      removeImage(index) {
        if (this.limit === 1) {
          URL.revokeObjectURL(this.previewUrl);
          this.previewUrl = "";
          this.$emit("update:url", ""); // 更新父组件的url
        } else {
          URL.revokeObjectURL(this.previewList[index]);
          this.previewList.splice(index, 1);
          this.list.splice(index, 1);
          this.$emit("update:list", this.list); // 更新父组件的list
        }
      }
    }
  };
</script>

<style scoped>
  .upload-wrapper {
    display: flex;
    gap: 10px;
  }
  .upload-preview {
    width: 148px;
    height: 148px;
    position: relative;
    border-radius: 6px;
    border: 1px dashed #d9d9d9;
    overflow: hidden;
  }
  .upload-preview img,
  .upload-preview video {
    width: 100%;
    height: 100%;
    object-fit: contain;
    border-radius: 6px;
  }
  .overlay {
    position: absolute;
    top: 0;
    right: 0;
    bottom: 0;
    left: 0;
    background-color: rgba(0, 0, 0, 0.5);
    color: #fff;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 6px;
    opacity: 0;
    transition: opacity 0.3s;
    i {
      cursor: pointer;
    }
  }
  .upload-preview:hover .overlay {
    opacity: 1;
    font-size: 16px;
  }
  .upload-button {
    width: 148px;
    height: 148px;
    display: flex;
    align-items: center;
    justify-content: center;
    border: 1px dashed #d9d9d9;
    border-radius: 6px;
    cursor: pointer;
    color: #8c939d;
    background-color: #fbfdff;
    transition: 0.2s;
    &:hover {
      border: 1px dashed #4395f9;
    }
  }
  .upload-button i {
    font-size: 24px;
  }
  .upload-loading {
    display: flex;
    flex-direction: column;
    align-items: center;
    color: #4395f9;
  }
</style>
